
import Vue from "vue";
import { mapActions } from "vuex";
import * as XLSX from "xlsx";

import dialogMixin from "@/common/mixins/dialogModal";
import Container from "@/components/Container.vue";
import LoadingRedirectPage from "@/components/LoadingRedirectPage.vue";
import CreateCompanyPage from "@/features/company/pages/CreateCompanyPage.vue";
import ThreeDotIcon from "@/components/icons/ThreeDotIcon.vue";
import DownloadIcon from "@/components/icons/DownloadIcon.vue";
import AddIcon2 from "@/components/icons/AddIcon2.vue";
import SearchIcon from "@/components/icons/SearchIcon.vue";

const STATUS_FILTER_OPTIONS = [
  { label: "All Status", value: "all" },
  { label: "Active", value: "active" },
  { label: "Expired", value: "expired" },
];

const TYPE_FILTER_OPTIONS = [
  { label: "All Type", value: "all" },
  { label: "Internal", value: "internal" },
  { label: "External", value: "external" },
];

const STORAGE_KEY = "companies_page_state";

export default Vue.extend({
  name: "CompaniesPage",
  mixins: [dialogMixin],
  components: { Container, LoadingRedirectPage, CreateCompanyPage, ThreeDotIcon, DownloadIcon, AddIcon2, SearchIcon },
  data() {
    return {
      allCompanies: [] as any[],
      isLoading: true,
      logoDefault: require("@/assets/images/default-factory.png"),
      searchInput: "",
      statusFilter: "active",
      typeFilter: "all",
      statusFilterOptions: STATUS_FILTER_OPTIONS,
      typeFilterOptions: TYPE_FILTER_OPTIONS,
    };
  },
  computed: {
    isAdmin(): boolean {
      return this.$store.getters.user?.role === "ADMIN";
    },
    isOwnerRvs(): boolean {
      return this.$store.getters.user?.role === "OWNER_RVS";
    },
    filteredCompanies(): any[] {
      let list = this.allCompanies;

      const q = (this.searchInput || "").trim().toLowerCase();
      if (q) {
        list = list.filter((c: any) => c.title?.toLowerCase().includes(q));
      }

      if (this.statusFilter !== "all") {
        const today = new Date();
        list = list.filter((c: any) => {
          const isExpired = c.end_date && new Date(c.end_date) < today;
          return this.statusFilter === "expired" ? isExpired : !isExpired;
        });
      }

      if (this.isAdmin && this.typeFilter !== "all") {
        list = list.filter((c: any) => (this.typeFilter === "internal" ? c.is_internal : !c.is_internal));
      }

      return list;
    },
    total(): number {
      return this.filteredCompanies.length;
    },
  },
  watch: {
    searchInput() {
      this.saveState();
    },
    statusFilter() {
      this.saveState();
    },
    typeFilter() {
      this.saveState();
    },
  },
  async created() {
    this.restoreState();
    await this.fetchCompanies();
  },
  methods: {
    ...mapActions(["getCompany", "getStructure"]),
    saveState() {
      sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          searchInput: this.searchInput,
          statusFilter: this.statusFilter,
          typeFilter: this.typeFilter,
        })
      );
    },
    restoreState() {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      if (!saved) return;
      try {
        const state = JSON.parse(saved);
        if (state.searchInput !== undefined) this.searchInput = state.searchInput;
        if (state.statusFilter) this.statusFilter = state.statusFilter;
        if (state.typeFilter) this.typeFilter = state.typeFilter;
      } catch (error) {
        // ignore invalid stored state
      }
    },
    async fetchCompanies() {
      try {
        this.isLoading = true;
        const response = await (this as any).$dep.companyUseCase.getCompanies();
        this.allCompanies = response;
      } catch (error) {
        this.allCompanies = [];
      } finally {
        this.isLoading = false;
      }
    },
    async onSelected(company: any) {
      this.isLoading = true;
      const { id } = company;
      sessionStorage.setItem("company_id", id);
      await this.getCompany();
      await this.getStructure();
      this.isLoading = false;
      this.$router.push({ name: "CompanyPage", params: { id } });
    },
    imagePathError(event: any) {
      event.target.src = this.logoDefault;
    },
    onSubmitModal(param: any) {
      if (param.action_type === "CREATE") {
        this.createCompany(param);
      }
      if (param.action_type === "EDIT") {
        this.updateCompany(param);
      }
    },
    async createCompany(param: any) {
      try {
        this.isLoading = true;
        await (this as any).$dep.companyUseCase.createCompany(param);
        this.$store.dispatch("openAlertMessage", {
          open: true,
          message: "Create company successfully",
          type: "success",
        });
        await this.fetchCompanies();
      } catch (error) {
        this.$store.dispatch("openAlertMessage", {
          open: true,
          message: error && error?.detail ? error.detail : "error: create company failed",
          type: "error",
        });
      } finally {
        this.isLoading = false;
      }
    },
    async updateCompany(param: any) {
      try {
        const { id, image_path, title, initial_code, description, start_date, end_date, rvs_upgrade, is_internal } = param;
        this.isLoading = true;
        await (this as any).$dep.companyUseCase.updateCompany(id, {
          title,
          description,
          initial_code,
          image_path,
          start_date,
          end_date,
          rvs_upgrade,
          is_internal,
        });
        this.$store.dispatch("openAlertMessage", {
          open: true,
          message: "Update company successfully",
          type: "success",
        });
        await this.fetchCompanies();
      } catch (error) {
        this.$store.dispatch("openAlertMessage", {
          open: true,
          message: error && error?.detail ? error.detail : "error: update company failed",
          type: "error",
        });
      } finally {
        this.isLoading = false;
      }
    },
    async updateInternalCompany(id: number, is_internal: boolean) {
      try {
        await (this as any).$dep.companyUseCase.updateInternalCompany(id, { is_internal });
        const target = this.allCompanies.find((c: any) => c.id === id);
        if (target) {
          target.is_internal = is_internal;
        }
      } catch (error) {
        this.$store.dispatch("openAlertMessage", {
          open: true,
          message: error && error?.detail ? error.detail : "error: update company failed",
          type: "error",
        });
      }
    },
    async deleteCompany(id: number) {
      try {
        const is = await (this as any).dialogDelete("Do you want to delete this company?");
        if (is) {
          this.isLoading = true;
          await (this as any).$dep.companyUseCase.deleteCompany(id);
          this.$store.dispatch("openAlertMessage", {
            open: true,
            message: "Delete company successfully",
            type: "success",
          });
          await this.fetchCompanies();
        }
      } catch (error) {
        this.$store.dispatch("openAlertMessage", {
          open: true,
          message: error && error?.detail ? error.detail : "error: delete company failed",
          type: "error",
        });
      } finally {
        this.isLoading = false;
      }
    },
    exportToExcel() {
      const today = new Date();
      const rows = this.filteredCompanies.map((c: any, index: number) => ({
        "No.": index + 1,
        "Company Name": c.title || "",
        "Initial Code": c.initial_code || "",
        Type: c.is_internal ? "Internal" : "External",
        Status: c.end_date && new Date(c.end_date) < today ? "Expired" : "Active",
        "Start Date": c.start_date || "",
        "End Date": c.end_date || "",
      }));
      const worksheet = XLSX.utils.json_to_sheet(rows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Companies");
      XLSX.writeFile(workbook, `CompanyList_${new Date().toISOString().slice(0, 10)}.xlsx`);
    },
    manageMember(id: number, title: string) {
      this.$router.push({ name: "CompanyMemberPage", params: { id, title } });
    },
    convertExpiredDate(date: any) {
      if (!date) return "-";
      const monthNames = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ];
      const dt = new Date(date);
      const dateToday = new Date();
      const diffDate = dt.getTime() - dateToday.getTime();
      const dfDays = diffDate / (1000 * 60 * 60 * 24);
      const day = `${dt.getDate()}`.padStart(2, "0");
      const month = monthNames[dt.getMonth()];
      const year = dt.getFullYear();
      if (dateToday < dt) {
        return `${day} ${month} ${year} (${dfDays.toFixed(0)} days left)`;
      }
      return `${day} ${month} ${year}`;
    },
    checkDateExpired(date: any) {
      if (!date) return false;
      const dt = new Date(date);
      const dateToday = new Date();
      const dfDays = (dt.getTime() - dateToday.getTime()) / (1000 * 60 * 60 * 24);
      return dfDays <= 30;
    },
  },
});
